import { useState } from "react";
import { signUp } from "aws-amplify/auth";
import { TextInput, PasswordInput, Checkbox } from "@mantine/core";
import { useForm } from "@mantine/form";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { z } from "zod";
import { usePostHog } from "posthog-js/react";
import { PRIVACY_POLICY, TERMS_OF_SERVICE } from "../../../routes/wix-routes";
import { AuthEvent } from "../../../data/auth-events";
import {
  emailSchema,
  passwordWithRequirementsSchema,
} from "./validation-schemas";
import PasswordRequirements from "./password-requirements";
import styles from "./claimable-authenticator-v2.module.scss";

const signUpFormSchema = z
  .object({
    email: emailSchema,
    password: passwordWithRequirementsSchema,
    confirmPassword: z.string(),
    agreeToTerms: z.boolean(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type SignUpFormValues = z.infer<typeof signUpFormSchema>;

type SignUpFormProps = {
  onSignUpSuccess: (email: string) => void;
  onSwitchToSignIn: () => void;
  prefilledEmail?: string;
};

const SignUpForm = ({
  onSignUpSuccess,
  onSwitchToSignIn,
  prefilledEmail,
}: SignUpFormProps) => {
  const posthog = usePostHog();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<SignUpFormValues>({
    initialValues: {
      email: prefilledEmail || "",
      password: "",
      confirmPassword: "",
      agreeToTerms: false,
    },
    validate: zod4Resolver(signUpFormSchema),
  });

  const handleSubmit = async (values: SignUpFormValues) => {
    if (!values.agreeToTerms) {
      form.setFieldError(
        "agreeToTerms",
        "You must agree to the Terms of Service and Privacy Policy",
      );
      return;
    }

    posthog?.capture(AuthEvent.SignUpSubmit);
    setIsSubmitting(true);
    setError(null);

    try {
      await signUp({
        username: values.email,
        password: values.password,
        options: {
          userAttributes: {
            email: values.email,
          },
          autoSignIn: true,
        },
      });

      posthog?.capture(AuthEvent.SignUpSuccess);
      onSignUpSuccess(values.email);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An error occurred during sign up";
      posthog?.capture(AuthEvent.SignUpError, { error: errorMessage });
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.signUpForm}>
      <img
        src="/images/logo/logo_black_large.svg"
        alt="Claimable"
        className={styles.formLogo}
      />
      <p className={styles.formEyebrow}>{"It's time to appeal!"}</p>
      <h1 className={styles.formTitle}>
        {"Create a Claimable account to continue your appeal"}
      </h1>
      <p className={styles.formSubtitle}>
        {
          "Creating an account helps save your progress and keeps your data secure."
        }
      </p>

      <form onSubmit={form.onSubmit(handleSubmit)} className={styles.form}>
        <TextInput
          label="Email"
          placeholder="you@example.com"
          size="lg"
          value={form.values.email}
          onChange={(e) => form.setFieldValue("email", e.currentTarget.value)}
          onBlur={() => form.validateField("email")}
          error={form.errors.email}
          disabled={Boolean(prefilledEmail)}
          classNames={{
            input: styles.mantineInput,
            label: styles.inputLabel,
          }}
        />

        <div className={styles.inputGroup}>
          <PasswordInput
            label="Password"
            placeholder="Create a password"
            size="lg"
            value={form.values.password}
            onChange={(e) =>
              form.setFieldValue("password", e.currentTarget.value)
            }
            error={
              form.isTouched("password") && form.errors.password ? true : false
            }
            classNames={{
              input: styles.mantineInput,
              label: styles.inputLabel,
            }}
          />
          <PasswordRequirements password={form.values.password} />
        </div>

        <PasswordInput
          label="Confirm password"
          placeholder="Confirm your password"
          size="lg"
          value={form.values.confirmPassword}
          onChange={(e) =>
            form.setFieldValue("confirmPassword", e.currentTarget.value)
          }
          onBlur={() => form.validateField("confirmPassword")}
          error={form.errors.confirmPassword}
          classNames={{
            input: styles.mantineInput,
            label: styles.inputLabel,
          }}
        />

        {error && <p className={styles.formError}>{error}</p>}

        <Checkbox
          checked={form.values.agreeToTerms}
          onChange={(event) =>
            form.setFieldValue("agreeToTerms", event.currentTarget.checked)
          }
          error={form.errors.agreeToTerms}
          label={
            <span className={styles.checkboxLabel}>
              {"I agree to the "}
              <a
                href={TERMS_OF_SERVICE}
                target="_blank"
                rel="noopener noreferrer"
              >
                {"Terms of Service"}
              </a>
              {" and "}
              <a
                href={PRIVACY_POLICY}
                target="_blank"
                rel="noopener noreferrer"
              >
                {"Privacy Policy"}
              </a>
            </span>
          }
        />

        <button
          type="submit"
          className={styles.primaryButton}
          disabled={isSubmitting}
          data-testid="sign-up-submit"
        >
          {isSubmitting ? "Creating account..." : "CREATE ACCOUNT"}
        </button>
      </form>

      <div className={styles.switchAuthMode}>
        <span>{"Have an account already? "}</span>
        <button
          type="button"
          onClick={onSwitchToSignIn}
          className={styles.switchLink}
          data-testid="switch-to-sign-in"
        >
          {"Log in"}
        </button>
      </div>
    </div>
  );
};

export default SignUpForm;
